<script async setup>
import { onMounted, onUnmounted, ref, watch, nextTick, computed } from "vue";
import FavoritesService, { FAVORITES_EVENT } from "../services/FavoritesService.js";

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  icons: {
    type: Array,
    default: () => [],
  },
  cart: {
    type: Object,
    default: () => ({}),
  },
  config: {
    type: Object,
    default: () => ({}),
  },
});

const search = ref("");
const isSearchActive = ref(false);
const showSearch = ref(false);
const inputWrapRef = ref(null);
const menu = ref(false);
const activeMobileIndex = ref(null);
const cartCount = ref(props.cart?.count || 0);

const onCartUpdated = (event) => {
  const payload = event?.detail || {};
  if (typeof payload.count === "number") {
    cartCount.value = payload.count;
  }
};

const updateFavoriteCount = () => {
    favoriteCount.value = FavoritesService.count();
};

const animateHeart = ref(false);
const favoriteCount = ref(FavoritesService.count());

watch(favoriteCount, (newVal, oldVal) => {
  if (newVal === oldVal) return;
  animateHeart.value = true;
  setTimeout(() => {
    animateHeart.value = false;
  }, 200);
});

const toggleMenu = () => { menu.value = !menu.value; };
const toggleMobileAccordion = (i) => { activeMobileIndex.value = activeMobileIndex.value === i ? null : i; };
const updateBodyScrollLock = () => { document.body.style.overflow = menu.value ? "hidden" : ""; };

watch(menu, updateBodyScrollLock);

onMounted(() => {
  window.addEventListener("kd:search:overlay-close", onOverlayClose);
  window.addEventListener("kd:search:set-term", onSetSearchTerm);
  updateFavoriteCount();
  window.addEventListener(FAVORITES_EVENT, updateFavoriteCount);
  window.addEventListener("kd:cart:update", onCartUpdated);
});

onUnmounted(() => {
  window.removeEventListener("kd:search:overlay-close", onOverlayClose);
  window.removeEventListener("kd:search:set-term", onSetSearchTerm);
  document.body.style.overflow = "";
  window.removeEventListener(FAVORITES_EVENT, updateFavoriteCount);
  window.removeEventListener("kd:cart:update", onCartUpdated);
});

const openMobileSearch = async () => {
  showSearch.value = true;
  isSearchActive.value = true;
  await nextTick();
  inputWrapRef.value?.querySelector?.("input")?.focus?.();
};

const openSearch = () => {
  window.dispatchEvent(new CustomEvent("kd:search:open"));
};

const closeSearch = () => {
  isSearchActive.value = false;
  showSearch.value = false;
  window.dispatchEvent(new CustomEvent("kd:search:close"));
};

watch(search, (val) => {
  window.dispatchEvent(
    new CustomEvent("kd:search:query", { detail: val || "" })
  );
});

const onOverlayClose = () => {
  isSearchActive.value = false;
  showSearch.value = false;
};

const onSetSearchTerm = (event) => {
  const term =
    typeof event?.detail === "string"
      ? event.detail
      : String(event?.detail || "");

  search.value = term;
  isSearchActive.value = true;
  showSearch.value = true;
};

const currentLanguageCode = computed(() => {
  const langs = props.config.languages || [];
  if (!langs.length) {
    return "";
  }

  const current = langs.find((l) => l.current);
  return (current || langs[0]).code;
});

const homeUrl = computed(() => props.config.home_url || "/");

const searchActionUrl = computed(() => {
  return props.config.search?.action_url || homeUrl.value;
});

const showLanguageMenuDesktop = ref(false);
const showLanguageMenuMobile = ref(false);

const toggleLanguageMenuDesktop = () => {
  showLanguageMenuDesktop.value = !showLanguageMenuDesktop.value;
};

const toggleLanguageMenuMobile = () => {
  showLanguageMenuMobile.value = !showLanguageMenuMobile.value;
};

watch(menu, (isOpen) => {
  if (isOpen) {
    showLanguageMenuDesktop.value = false;
    showLanguageMenuMobile.value = false;
  }
});
</script>

<template>
    <header class="fixed top-0 left-0 w-full z-[110] text-black flex flex-col" :class="{ 'h-svh overflow-hidden': menu }">
        <div class="bg-ts-white w-full shadow-ts-xl-bottom relative z-[100]">
            <div class="max-w-ts-content mx-auto px-4 flex justify-between py-2">
                <a v-show="!isSearchActive" :href="homeUrl" class="z-50 notebook:hidden">
                    <kd-icon-logo fill="black" class="w-25 xs:w-30 h-10" />
                </a>
                <a :href="homeUrl" class="z-50 hidden notebook:inline-block">
                    <kd-icon-logo fill="black" class="w-25 xs:w-30 h-10" />
                </a>  
                 
                <div class="flex items-center max-h-10 gap-6 notebook:flex-none" :class="(isSearchActive || showSearch) ? 'flex-1 min-w-0' : ''">
                    <!-- Favoriten -->
                    <a 
                        :href="props.config.favorites.url" 
                        v-show="!isSearchActive && !showLanguageMenuDesktop" 
                        class="relative flex items-center gap-1 cursor-pointer transition-opacity"
                    >
                        <kd-icon-heart fill="black" class="w-4 xs:w-5 notebook:w-3.5" />
                        <span class="hidden text-sm notebook:inline-block">
                            {{ props.config.favorites.label }}
                        </span>                    
                        <span
                            v-if="favoriteCount > 0"
                            :class="[
                                'bg-white border border-black text-black size-3 xs:size-4 rounded-full leading-none text-[10px] xs:text-xs  flex items-center justify-center pointer-events-none absolute',
                                'top-3 -right-2 xs:top-2 xs:-right-3',
                                'notebook:top-3 notebook:-right-4',
                                'transition-transform duration-200',
                                animateHeart ? 'scale-110 -translate-y-1' : 'scale-100 translate-y-0'
                            ]"
                        >
                            {{ favoriteCount }}
                        </span>
                    </a>
                    <!-- Warenkorb -->
                    <a 
                        :href="props.cart.url"
                        :title="props.cart.label"
                        :aria-label="props.cart.label"
                        v-show="!isSearchActive && !showLanguageMenuDesktop"
                        class="relative flex items-center gap-1 cursor-pointer transition-opacity"
                    >
                        <kd-icon-cart class="w-5 xs:w-6 notebook:w-4 z-50" fill="black" />
                    
                        <span class="hidden text-sm notebook:inline-block">
                            {{ props.cart.label }}
                        </span>                    
                        <span
                            v-if="cartCount > 0"
                            class="bg-white z-[60] border border-black text-black size-3 xs:size-4 rounded-full
                                   leading-none text-[10px] xs:text-xs flex items-center justify-center pointer-events-none
                                   absolute top-3 -right-2 xs:top-2 xs:-right-3 notebook:top-3 notebook:-right-4"
                        >
                            {{ cartCount }}
                        </span>
                    </a>
                    <!-- Search -->
                    <div
                        class="flex items-center gap-0 notebook:flex-none"
                        :class="(isSearchActive || showSearch) ? 'flex-1 min-w-0' : ''"
                        v-show="!showLanguageMenuDesktop"
                    >
                        <button
                            class="notebook:hidden flex items-center justify-center z-50"
                            @click="openMobileSearch"
                            v-show="!showSearch"
                            :aria-label="props.config.search.open_aria_label"
                        >
                            <kd-icon-search fill="black" class="w-4 xs:w-5" />
                        </button>
                        <form
                            role="search"
                            method="get"
                            :action="searchActionUrl"
                            autocomplete="off"
                            @focusin="isSearchActive = true; openSearch()"
                            class="z-50 min-w-0 py-2 notebook:py-0 overflow-hidden ease-out
                                   notebook:transition-width notebook:duration-300"
                            :class="[
                                showSearch
                                    ? 'opacity-100 max-w-full transition-[max-width,opacity] duration-300'
                                    : 'opacity-0 max-w-0 pointer-events-none transition-none',
                                'notebook:block notebook:opacity-100 notebook:max-w-none notebook:pointer-events-auto',
                                isSearchActive ? 'flex-1 w-full notebook:w-90' : 'w-40 notebook:w-55 notebook:flex-none'
                            ]"
                        >
                            <div id="search" class="relative flex items-center gap-2" ref="inputWrapRef">
                                <div class="relative flex-1">
                                    <kd-icon-search fill="black" size="15" class="absolute top-1.25 right-2 z-50" />
                                    <kd-input
                                        name="s"
                                        :placeholder="props.config.search.placeholder"
                                        v-model:value="search"
                                        type="search"
                                        additional-class="w-full appearance-none [-webkit-appearance:none] [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
                                    />
                                </div>
                                <div v-show="isSearchActive" class="h-4 border-l border-ts-black transition-opacity"></div>
                                <kd-icon-x
                                    v-show="isSearchActive || showSearch"
                                    fill="black"
                                    size="15"
                                    :aria-label="props.config.search.close_aria_label"
                                    :title="props.config.search.close_aria_label"
                                    class="cursor-pointer transition-opacity"
                                    @click="closeSearch"
                                />
                            </div>
                        </form>
                    </div>
                    <!-- Language -->
                    <div v-show="!isSearchActive" class="items-center gap-5 transition-opacity hidden notebook:flex">
                        <transition
                            enter-active-class="transition-all duration-200 ease-out"
                            enter-from-class="opacity-0 translate-x-6"
                            enter-to-class="opacity-100 translate-x-0"
                        >
                            <div
                                v-if="showLanguageMenuDesktop && props.config.languages && props.config.languages.length"
                                class="flex items-center gap-5"
                            >
                                <template v-for="(lang, index) in props.config.languages" :key="lang.code">
                                    <a
                                        :href="lang.url"
                                        class="flex items-center gap-2 text-sm uppercase cursor-pointer"
                                        :class="lang.current ? 'font-bold' : 'opacity-60 hover:opacity-100'"
                                    >
                                        <img
                                            v-if="lang.flag_url"
                                            :src="lang.flag_url"
                                            :alt="lang.label || lang.code"
                                            class="size-3 rounded-full object-cover"
                                        >
                                        {{ lang.code }}
                                    </a>
                                    <div v-if="index < props.config.languages.length - 1" class="border-l border-ts-black h-4"></div>
                                </template>
                            </div>
                        </transition>
                        <div class="border-l border-ts-black h-4"></div>
                        <button
                            type="button"
                            class="flex items-center gap-1 cursor-pointer"
                            @click="toggleLanguageMenuDesktop"
                            :aria-expanded="showLanguageMenuDesktop ? 'true' : 'false'"
                            aria-haspopup="true"
                        >
                            <kd-icon-language fill="black" size="15" />   
                            <span class="text-sm uppercase">
                                {{ currentLanguageCode }}
                            </span>
                        </button>
                    </div>
                    <!-- Burger Menu Button -->
                    <div v-show="!isSearchActive" class="relative size-4 xs:size-7 z-50 cursor-pointer notebook:hidden" @click="toggleMenu">
                        <span
                            class="absolute transition-all duration-200 origin-center top-1/2 left-1/2 -translate-x-1/2 rounded-lg w-7 h-[3px] bg-black"
                            :class="{
                                '-translate-y-1/2 -rotate-45': menu,
                                'translate-y-[-13px]': !menu
                            }"
                        />
                        <span
                            class="absolute transition-all duration-200 origin-center top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-lg w-7 h-[3px] bg-black"
                            :class="{ 'opacity-0': menu }"
                        />
                        <span
                            class="absolute transition-all duration-200 origin-center top-1/2 left-1/2 -translate-x-1/2 rounded-lg w-7 h-[3px] bg-black"
                            :class="{
                                '-translate-y-1/2 rotate-45': menu,
                                'translate-y-[9px]': !menu
                            }"
                        />
                    </div>
                </div>
            </div>
        </div>
        <!-- Labels -->
        <div class="relative z-40 hidden notebook:block">
            <div
                class="bg-ts-white border-t border-ts-black shadow-ts-xl-bottom relative z-40
                       transition-[max-height,opacity,transform] duration-300 ease-out
                       unscrolled:max-h-12 unscrolled:opacity-100 unscrolled:translate-y-0
                       scrolled-up:max-h-12 scrolled-up:opacity-100 scrolled-up:translate-y-0
                       scrolled-down:max-h-0 scrolled-down:opacity-0 scrolled-down:-translate-y-1
                       scrolled-down:pointer-events-none"
            >
                <div class="max-w-ts-content mx-auto px-4 flex justify-between gap-6 [&:has(.nav-item:hover)_.nav-label]:opacity-50 [&:has(.nav-item:hover)_.img-label]:opacity-50">
                    <div class="pointer-events-auto absolute inset-0 z-40 hidden scrolled-down:block"></div>
                    <div v-for="item in props.items" :key="item.url" class="nav-item group/item flex items-center gap-1 transition-opacity">
                        <a
                            :style="item.fields?.label_color ? { color: item.fields.label_color } : null"
                            :title="item.title || item.label"
                            :href="item.url"
                            class="nav-label flex gap-1 text-sm py-4 transition
                                   group-hover/item:!opacity-100 group-hover/item:font-bold
                                   hover:px-12 hover:-mx-12
                                   scrolled-down:!px-0 scrolled-down:!-mx-0 scrolled-down:!font-normal
                                   scrolled-down:pointer-events-none"
                        >
                            <img 
                                v-if="item.fields.icon"
                                :src="item?.fields?.icon?.sizes?.thumbnail"
                                :alt="item.title || item.label"
                                class="img-label size-3.5 object-contain group-hover/item:!opacity-100"
                            >
                            {{ item.label }}
                        </a>
                        <!-- Dropdown Menu Desktop -->
                        <div
                            v-if="item.children && item.children.length"
                            class="absolute top-full inset-x-0 w-screen z-30
                                   -translate-y-6 opacity-0 transition-all duration-300 ease-in-out
                                   pointer-events-none invisible
                                   group-hover/item:translate-y-0 group-hover/item:opacity-100
                                   group-hover/item:pointer-events-auto group-hover/item:visible scrolled-down:!-translate-y-6 scrolled-down:!opacity-0
                                   scrolled-down:!pointer-events-none scrolled-down:!invisible"
                        >
                            <div class="w-full bg-ts-white shadow-ts-combo max-h-[calc(100vh-130px)] overflow-y-auto">
                                <div class="max-w-ts-content mx-auto px-4 py-6">
                                    <div class="grid grid-cols-5 gap-4">
                                        <div
                                            v-if="item.fields.headline || item.fields.image_2"
                                            class="bg-ts-black text-white rounded-2xl p-4 w-full overflow-hidden"
                                            :class="item.fields.image_2 && item.fields.box_colspan !== '2' ? 'col-span-3' : 'col-span-2'"
                                        >
                                            <div :class="[' gap-4 h-full', item.fields.image_2 && item.fields.box_colspan !== '2' ? 'grid grid-cols-3' : 'grid grid-cols-2']">
                                                <div
                                                    class="flex flex-col justify-start items-start h-full"
                                                    :class="item.fields.image_2 && item.fields.box_colspan === '2' ? 'col-span-1' : 'col-span-2'"
                                                >
                                                    <p v-if="item.fields.headline" class="text-base font-semibold mb-2">
                                                        {{ item.fields.headline }}
                                                    </p>
                                                    <p class="text-sm line-clamp-3 xxlbook:line-clamp-5 mb-3" v-if="item.fields.text">
                                                        {{ item.fields.text }}
                                                    </p>
                                                    <kd-button
                                                        v-if="item.fields.button_label"
                                                        :href="item.fields.button_link.url"
                                                        :target="item.fields.button_link.target || '_self'"
                                                        variant="white"
                                                        class="mt-auto"
                                                    >
                                                        {{ item.fields.button_label }}
                                                    </kd-button>
                                                </div>
                                                <div
                                                    v-if="item.fields.image_2"
                                                    class="rounded-2xl overflow-hidden h-full"
                                                    :style="{ backgroundImage: `url(${item.fields.image_2.sizes?.medium || item.fields.image_2.url})`,
                                                              backgroundSize: 'cover',
                                                              backgroundPosition: 'center'
                                                    }"
                                                ></div>
                                            </div>
                                        </div>
                                        <div
                                            v-for="child in item.children"
                                            :key="child.url || child.label"
                                            class="group/card"
                                        >
                                            <div
                                                class="relative rounded-2xl size-full overflow-hidden aspect-[1/1]"
                                                :style="child.fields?.image ? { backgroundImage: `url(${child.fields.image.sizes?.medium || child.fields.image.url})`,
                                                                                backgroundSize: 'cover',
                                                                                backgroundPosition: 'center' } : {}"
                                            >
                                                <div class="absolute bottom-3 left-3 right-4">
                                                    <kd-button :href="child.url" variant="white_hover">
                                                        {{ child.label }}
                                                    </kd-button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- Dropdown Mobile Menu -->
        <nav v-if="menu" class="notebook:hidden flex-1 min-h-0 overflow-y-auto transition-transform duration-200 ease-in-out z-40" :class="menu ? 'translate-y-0' : '-translate-y-full'">
            <div class="bg-ts-white text-black shadow-ts-xl-bottom relative z-40">
                <div class="max-w-ts-content mx-auto px-4 flex flex-col gap-4 pb-[max(16px,env(safe-area-inset-bottom))]">
                    <div class="sticky top-0 z-50 bg-ts-white -mx-4 px-4 py-3 border-b shadow-ts-xl-bottom">
                        <div class="flex items-center justify-end gap-4">
                            <!-- Language -->
                            <div v-if="showLanguageMenuMobile && props.config.languages && props.config.languages.length" class="flex items-center gap-4">
                                <template v-for="(lang) in props.config.languages" :key="lang.code">
                                    <div class="flex items-center gap-2 ">
                                        <a
                                            :href="lang.url"
                                            class="flex items-center gap-2 text-sm uppercase cursor-pointer"
                                            :class="lang.current ? 'font-bold' : 'opacity-60 hover:opacity-100'"
                                        >
                                            <img
                                                v-if="lang.flag_url"
                                                :src="lang.flag_url"
                                                :alt="lang.label || lang.code"
                                                class="size-3 rounded-full object-cover"
                                            >
                                            {{ lang.code }}
                                        </a>
                                        <div class="border-r border-ts-black h-4"></div>
                                    </div>
                                </template>
                            </div>

                            <button
                                type="button"
                                class="flex items-center gap-1 cursor-pointer"
                                @click="toggleLanguageMenuMobile"
                                :aria-expanded="showLanguageMenuMobile ? 'true' : 'false'"
                                aria-haspopup="true"
                            >
                                <kd-icon-language fill="black" size="18" />
                                <span class="text-sm uppercase">
                                    {{ currentLanguageCode }}
                                </span>
                            </button>
                        </div>
                    </div>
                    <div
                        v-for="(item, index) in props.items"
                        :key="item.url || index"
                        class="border-b border-ts-black pb-2"
                    >
                        <component
                            :is="item.children && item.children.length ? 'div' : 'a'"
                            :href="!item.children || !item.children.length ? item.url : undefined"
                            :title="item.title ? item.title : item.label"
                            class="w-full flex items-center justify-between text-base font-semibold cursor-pointer pr-2 py-3"
                            @click="item.children && item.children.length ? toggleMobileAccordion(index) : null"
                        >
                            <span>{{ item.label }}</span>
                            <kd-icon-chevron-down
                                v-if="item.children && item.children.length"
                                class="transition-transform duration-200"
                                :class="activeMobileIndex === index ? 'rotate-180' : ''"
                                size="20"
                                fill="black"
                            />
                        </component>
                  
                        <div
                            v-if="item.children && item.children.length && activeMobileIndex === index"
                            class="mt-2 pb-4"
                        >
                            <div class="grid grid-cols-2 phone:grid-cols-3 phonexl:grid-cols-4 gap-3">
                                <div
                                    v-if="item.fields.headline || item.fields.image_2"
                                    class="bg-ts-black text-white rounded-2xl p-4 w-full overflow-hidden"
                                    :class="item.fields.image_2 && item.fields.box_colspan !== '2' ? 'col-span-2 phonexl:col-span-3' : 'col-span-2'"
                                >
                                    <div :class="['gap-4 items-start', item.fields.image_2 && item.fields.box_colspan !== '2' ? 'grid grid-cols-2 phonexl:grid-cols-3' : 'grid grid-cols-2']">
                                        <div 
                                            class="flex flex-col justify-between items-start h-full"
                                            :class="item.fields.image_2 && item.fields.box_colspan !== '2' ? 'col-span-1 phonexl:col-span-2' : item.fields.image_2 ? 'col-span-1' : 'col-span-2'"
                                        >
                                            <p v-if="item.fields.headline" class="text-base font-semibold mb-2">
                                                {{ item.fields.headline }}
                                            </p>
                                            <p class=" mb-3" v-if="item.fields.text">
                                                {{ item.fields.text }}
                                            </p>
                                            <kd-button
                                                v-if="item.fields.button_label"
                                                :href="item.fields.button_link.url"
                                                :target="item.fields.button_link.target || '_self'"
                                                variant="white"
                                                class="mt-auto"
                                            >
                                                {{ item.fields.button_label }}
                                            </kd-button>
                                        </div>
                                        <div
                                            v-if="item.fields.image_2"
                                            class="rounded-2xl overflow-hidden aspect-[1/1] w-full self-start"
                                            :style="{
                                                backgroundImage: `url(${item.fields.image_2.sizes?.thumbnail || item.fields.image_2.url})`,
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'center'
                                            }"
                                        ></div>
                                    </div>
                                </div>
                            
                                <div
                                    v-for="child in item.children"
                                    :key="child.url || child.label"
                                    class="group/card"
                                >
                                    <div
                                        class="relative rounded-2xl overflow-hidden aspect-[1/1] w-full"
                                        :style="child.fields?.image ? {
                                            backgroundImage: `url(${child.fields.image.sizes?.thumbnail || child.fields.image.url})`,
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'center'
                                        } : {}"
                                    >
                                        <div class="absolute bottom-3 inset-x-3">
                                            <kd-button :href="child.url" variant="white_hover">
                                                {{ child.label }}
                                            </kd-button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <span class="flex-1 fixed inset-0 bg-black/50" @click="toggleMenu" />
        </nav>
        <kd-cart-sidebar :cart="props.cart"></kd-cart-sidebar>
    </header>
</template>